import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NotebookProvider, useNotebook } from '@/contexts/NotebookContext';
import NotebookCell from '@/components/notebooks/NotebookCell';
import NotebookToolbar from '@/components/notebooks/NotebookToolbar';
import NotebookHelpPanel from '@/components/notebooks/NotebookHelpPanel';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, AlertCircle } from 'lucide-react';

// =============================================================================
// Inner Component (uses context)
// =============================================================================

function NotebookPageContent() {
  const { notebookId } = useParams<{ notebookId: string }>();
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const {
    state: { notebook, isLoading, error, selectedCellId, executingCellId },
    loadNotebook,
    addCell,
    selectCell,
    runCell,
  } = useNotebook();

  // Load notebook on mount
  useEffect(() => {
    if (notebookId) {
      loadNotebook(notebookId);
    }
  }, [notebookId, loadNotebook]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;

      // Skip shortcuts when focus is inside Monaco editor or other inputs
      const isInEditor = target.closest('textarea, input, [contenteditable], .monaco-editor');
      if (isInEditor) {
        // Only allow Escape to pass through for deselecting
        if (e.key === 'Escape') {
          selectCell(null);
        }
        return;
      }

      // Shift+Enter: Run current cell
      if (e.shiftKey && e.key === 'Enter' && selectedCellId) {
        e.preventDefault();
        runCell(selectedCellId);
      }

      // Escape: Deselect cell
      if (e.key === 'Escape') {
        selectCell(null);
      }

      // B: Add code cell below
      if (e.key === 'b' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        addCell('code');
      }

      // M: Add markdown cell below
      if (e.key === 'm' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        addCell('markdown');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCellId, runCell, selectCell, addCell]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="w-12 h-12 text-destructive" />
        <p className="text-lg text-destructive">{error}</p>
        <Button variant="outline" onClick={() => navigate('/notebooks')}>
          Back to Notebooks
        </Button>
      </div>
    );
  }

  // No notebook loaded
  if (!notebook) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Notebook not found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden">
      {/* Toolbar */}
      <NotebookToolbar
        onToggleHelp={() => setShowHelp(!showHelp)}
        showHelp={showHelp}
      />

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Cells */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto space-y-2">
            {notebook.cells.map((cell) => (
              <NotebookCell
                key={cell.id}
                cell={cell}
                isSelected={selectedCellId === cell.id}
                isExecuting={executingCellId === cell.id}
              />
            ))}

            {/* Add cell button at bottom */}
            <div className="flex justify-center py-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => addCell('code')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Cell
              </Button>
            </div>
          </div>
        </div>

        {/* Help Panel */}
        {showHelp && (
          <NotebookHelpPanel onClose={() => setShowHelp(false)} />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Page Component (provides context)
// =============================================================================

export default function NotebookPage() {
  return (
    <NotebookProvider>
      <NotebookPageContent />
    </NotebookProvider>
  );
}
